import { TournamentPlan } from 'modules/organizer/domain/interfaces/tournament-plan';
import { formatRublesFromMinorUnits } from 'modules/organizer/domain/utils/money';
import { formatSubscriptionPeriod } from 'modules/organizer/domain/utils/subscription-period';
import { buyPlanFormValidator, BuyPlanFormValues } from 'modules/organizer/domain/validators/wallet-topup.validator';
import React from 'react';
import { Form, Formik, FormikHelpers } from 'formik';
import UDButton from 'ud-ui/components/button';
import UDText from 'ud-ui/components/text';
import { theme } from 'styles/theme/theme';
import { SubscriptionPlanPicker } from './subscription-topup-form';

interface SubscriptionBuyPlanFormProps {
  plans: TournamentPlan[];
  initialPlanId: number;
  balanceInMinorUnits: number;
  isSubmitting: boolean;
  error: string | null;
  onSubmit: (values: BuyPlanFormValues) => Promise<void>;
}

export const SubscriptionBuyPlanForm: React.FC<SubscriptionBuyPlanFormProps> = ({
  plans,
  initialPlanId,
  balanceInMinorUnits,
  isSubmitting,
  error,
  onSubmit,
}) => {
  const handleSubmit = async (values: BuyPlanFormValues, helpers: FormikHelpers<BuyPlanFormValues>) => {
    try {
      await onSubmit(values);
    } finally {
      helpers.setSubmitting(false);
    }
  };

  return (
    <Formik<BuyPlanFormValues>
      initialValues={{ planId: initialPlanId }}
      enableReinitialize
      validationSchema={buyPlanFormValidator}
      onSubmit={handleSubmit}
    >
      {({ values, setFieldValue, isSubmitting: formSubmitting }) => {
        const selected = plans.find((plan) => plan.id === values.planId);
        const canAfford = !!selected && balanceInMinorUnits >= selected.priceInMinorUnits;

        return (
          <Form className="flex flex-col gap-4">
            <UDText type="headline">Подключить тариф</UDText>
            <UDText type="body-text" color={theme.colors.Gray}>
              Оплата списывается с баланса. Если средств не хватает — сначала пополните кошелёк.
            </UDText>
            <SubscriptionPlanPicker
              plans={plans}
              selectedPlanId={values.planId}
              locked={false}
              onSelect={(planId) => setFieldValue('planId', planId)}
            />
            {selected && !canAfford && (
              <UDText type="footnote" color={theme.colors.CaseErrorText}>
                Не хватает {formatRublesFromMinorUnits(selected.priceInMinorUnits - balanceInMinorUnits)} ₽. Пополните
                баланс.
              </UDText>
            )}
            {error && (
              <UDText type="footnote" color={theme.colors.CaseErrorText}>
                {error}
              </UDText>
            )}
            <UDButton
              type="submit"
              variant="primary"
              loading={isSubmitting || formSubmitting}
              disabled={!values.planId || !canAfford}
            >
              Оплатить с баланса
              {selected ? ` · ${formatRublesFromMinorUnits(selected.priceInMinorUnits)} ₽` : ''}
            </UDButton>
          </Form>
        );
      }}
    </Formik>
  );
};
